/**
 * Web Audio bus graph: child buses → master (+ soft limiter) → destination.
 */

import {
  AUDIO_CHILD_BUS_IDS,
  type AudioBusId,
  type AudioChildBusId,
  type AudioSettings,
  effectiveBusGain,
} from "./settings";

export type BusGainOptions = {
  /** Linear multiplier on music bus (ducking). Default 1. */
  musicDuck?: number;
  /** Linear multiplier on ambient bus (ducking). Default 1. */
  ambientDuck?: number;
  /**
   * Linear multiplier on lie claim VO bus.
   * Scripture / proclaim ducks this hard so the Word stays primary.
   */
  lieVoDuck?: number;
  /** Smooth ramp time for duckable buses (seconds). 0 = immediate. */
  rampSec?: number;
};

export interface AudioBusGraph {
  readonly context: AudioContext;
  readonly master: GainNode;
  readonly limiter: DynamicsCompressorNode;
  readonly buses: Readonly<Record<AudioChildBusId, GainNode>>;
  getBus(id: AudioBusId): GainNode;
  applySettings(settings: AudioSettings, opts?: BusGainOptions): void;
  dispose(): void;
}

/**
 * Soft master limiter (not a brickwall) so peaks stay gentle.
 * Child bus gains already fold in master volume via applySettings.
 */
export function createBusGraph(context: AudioContext): AudioBusGraph {
  const master = context.createGain();
  master.gain.value = 1;

  const limiter = context.createDynamicsCompressor();
  // Gentle ceiling
  limiter.threshold.value = -6;
  limiter.knee.value = 12;
  limiter.ratio.value = 4;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.15;

  master.connect(limiter);
  limiter.connect(context.destination);

  const buses = {} as Record<AudioChildBusId, GainNode>;
  for (const id of AUDIO_CHILD_BUS_IDS) {
    const g = context.createGain();
    g.gain.value = 0;
    g.connect(master);
    buses[id] = g;
  }

  const graph: AudioBusGraph = {
    context,
    master,
    limiter,
    buses,
    getBus(id: AudioBusId): GainNode {
      if (id === "master") return master;
      return buses[id];
    },
    applySettings(settings: AudioSettings, opts?: BusGainOptions): void {
      // Master is 1; mute + channel volumes live on child gains (master*child).
      // Duck multiplies music/ambient/lieVo (Reveal / Scripture attention).
      master.gain.value = 1;
      const musicDuck = opts?.musicDuck ?? 1;
      const ambientDuck = opts?.ambientDuck ?? 1;
      const lieVoDuck = opts?.lieVoDuck ?? 1;
      const ramp = opts?.rampSec ?? 0;
      const now = context.currentTime;

      for (const id of AUDIO_CHILD_BUS_IDS) {
        let g = effectiveBusGain(settings, id);
        if (id === "music") g *= musicDuck;
        if (id === "ambient") g *= ambientDuck;
        if (id === "lieVo") g *= lieVoDuck;

        const param = buses[id].gain;
        const duckable =
          id === "music" || id === "ambient" || id === "lieVo";
        if (ramp > 0 && duckable) {
          try {
            param.cancelScheduledValues(now);
            param.setValueAtTime(param.value, now);
            param.setTargetAtTime(g, now, ramp / 3);
          } catch {
            param.value = g;
          }
        } else {
          param.value = g;
        }
      }
    },
    dispose(): void {
      try {
        for (const id of AUDIO_CHILD_BUS_IDS) {
          buses[id].disconnect();
        }
        master.disconnect();
        limiter.disconnect();
      } catch {
        /* already disconnected */
      }
    },
  };

  return graph;
}
